import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SettingsPage.module.css';
import { Header } from '../../components/layout/Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usersAPI } from '../../services/api';

interface Preferences {
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  language: string;
  theme: string;
}

export const SettingsPage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [preferences, setPreferences] = useState<Preferences>({
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    language: 'nl',
    theme: 'light',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const prefs = (user as any).preferences;
      if (prefs) {
        setPreferences({
          notifications: {
            email: prefs.notifications?.email ?? true,
            push: prefs.notifications?.push ?? true,
            sms: prefs.notifications?.sms ?? false,
          },
          language: prefs.language || 'nl',
          theme: prefs.theme || 'light',
        });
      }
    }
  }, [user]);

  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);
    try {
      await usersAPI.update(user.id, { preferences });
      await refreshUser();
      toast.success('Instellingen opgeslagen', 'Je voorkeuren zijn bijgewerkt.');
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = (key: 'email' | 'push' | 'sms') => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
  };

  if (!user) return null;

  return (
    <div className={styles.settingsPage}>
      <Header
        user={user}
        onLogout={logout}
        showProjectSelector={false}
      />

      <div className={styles.settingsBody}>
        <div className={styles.settingsContent}>
          <a
            href="/projects"
            className={styles.backLink}
            onClick={(e) => { e.preventDefault(); navigate('/projects'); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Terug naar projecten
          </a>

          <h1 className={styles.pageTitle}>Instellingen</h1>
          <p className={styles.pageSubtitle}>Beheer je voorkeuren en meldingsinstellingen</p>

          {/* Notifications */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Meldingen</h2>

            <div className={styles.toggleRow}>
              <div className={styles.toggleLabel}>
                <span className={styles.toggleName}>E-mailmeldingen</span>
                <span className={styles.toggleDescription}>Ontvang updates en meldingen per e-mail</span>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={preferences.notifications.email}
                  onChange={() => handleNotificationToggle('email')}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            <div className={styles.toggleRow}>
              <div className={styles.toggleLabel}>
                <span className={styles.toggleName}>Pushmeldingen</span>
                <span className={styles.toggleDescription}>Ontvang meldingen in de browser</span>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={preferences.notifications.push}
                  onChange={() => handleNotificationToggle('push')}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            <div className={styles.toggleRow}>
              <div className={styles.toggleLabel}>
                <span className={styles.toggleName}>SMS-meldingen</span>
                <span className={styles.toggleDescription}>Ontvang urgente meldingen via SMS</span>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={preferences.notifications.sms}
                  onChange={() => handleNotificationToggle('sms')}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>
          </div>

          {/* Language & Theme */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Weergave</h2>

            <div className={styles.formGroup}>
              <label htmlFor="language">Taal</label>
              <select
                id="language"
                value={preferences.language}
                onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
              >
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="theme">Thema</label>
              <select
                id="theme"
                value={preferences.theme}
                onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
              >
                <option value="light">Licht</option>
                <option value="dark">Donker</option>
              </select>
            </div>
          </div>

          {/* Save */}
          <div className={styles.formActions}>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Opslaan...' : 'Instellingen opslaan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
